// <s-accordion>: FAQ list where only one answer is open at a time.
// Opening a question slowly closes the one that was open, with a smooth height glide.
const DURATION = 380;
const EASING = 'cubic-bezier(0.32, 0.72, 0, 1)';
const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

class SAccordion extends HTMLElement {
  // Page position from just before each item opened, so closing it returns the shopper to where they were.
  #returnTo = new WeakMap();

  connectedCallback() {
    // A fresh controller per connection so a re-attached accordion never ends up with two click handlers (which open and instantly re-close).
    this.controller?.abort();
    this.controller = new AbortController();
    this.addEventListener('click', (event) => {
      const summary = event.target.closest('summary');
      if (!summary || !this.contains(summary)) return;
      const item = summary.closest('[data-accordion-item]');
      if (!item) return;
      event.preventDefault();
      if (item.open && !item.dataset.closing) {
        this.#close(item);
        if (this.hasAttribute('data-scroll-on-open')) this.#returnToSaved(item);
      } else {
        // Height that will disappear above this item as other open answers close (so the scroll target is exact).
        let collapsingAbove = 0;
        // Switching between items keeps the position from before the first one opened.
        let returnTop = window.scrollY;
        this.querySelectorAll('[data-accordion-item][open]').forEach((other) => {
          if (other === item) return;
          if (this.#returnTo.has(other)) returnTop = this.#returnTo.get(other);
          this.#returnTo.delete(other);
          if (other.compareDocumentPosition(item) & Node.DOCUMENT_POSITION_FOLLOWING) {
            const c = this.#content(other);
            collapsingAbove += c ? c.offsetHeight : 0;
          }
          this.#close(other);
        });
        if (this.hasAttribute('data-scroll-on-open')) this.#returnTo.set(item, returnTop);
        this.#open(item);
        if (this.hasAttribute('data-scroll-on-open')) this.#scrollToItem(item, collapsingAbove);
      }
    }, { signal: this.controller.signal });
  }

  disconnectedCallback() {
    this.controller?.abort();
    clearTimeout(this.scrollCheck);
  }

  // Space covered at the top of the screen: the sticky header plus the menu bar under it.
  // The menu bar slides back in whenever the page scrolls up, so it is always counted (it never covers the text).
  #coveredTop() {
    const header = document.querySelector('.sticky.top-0');
    if (!header) return 0;
    const headerTop = Math.max(0, header.getBoundingClientRect().top);
    const sub = header.querySelector('[data-subheader]');
    const subInner = sub && sub.querySelector('[data-subheader-inner]');
    const subHeight = subInner ? subInner.offsetHeight : 0;
    return headerTop + header.offsetHeight + subHeight;
  }

  // Glide the page so the opened row sits just below the header and menu bar, then re-check once the
  // open animation has settled and correct if the top of the row ended up off screen.
  #scrollToItem(item, collapsingAbove) {
    // With data-scroll-to-start the whole group is the anchor, so rows above the opened one never hide under the sticky header.
    const anchor = this.hasAttribute('data-scroll-to-start') ? this : item;
    if (anchor === this) collapsingAbove = 0;
    const smooth = reduceMotion() ? 'auto' : 'smooth';
    const target = () => anchor.getBoundingClientRect().top + window.scrollY - this.#coveredTop() - 8;
    window.scrollTo({ top: Math.max(0, target() - collapsingAbove), behavior: smooth });
    clearTimeout(this.scrollCheck);
    this.scrollCheck = setTimeout(() => {
      const offBy = anchor.getBoundingClientRect().top - (this.#coveredTop() + 8);
      if (offBy < -2 || offBy > 40) window.scrollTo({ top: Math.max(0, target()), behavior: smooth });
    }, DURATION + 150);
  }

  // Glide back to where the page was when this item was opened.
  #returnToSaved(item) {
    clearTimeout(this.scrollCheck);
    const top = this.#returnTo.get(item);
    this.#returnTo.delete(item);
    if (top === undefined) return;
    window.scrollTo({ top: Math.max(0, top), behavior: reduceMotion() ? 'auto' : 'smooth' });
  }

  #content(item) {
    return item.querySelector('[data-accordion-content]');
  }

  #open(item) {
    const content = this.#content(item);
    // If it was mid-close, continue from its current height instead of snapping back to zero.
    const wasClosing = Boolean(item.dataset.closing);
    const start = wasClosing && content ? content.getBoundingClientRect().height : 0;
    delete item.dataset.closing;
    item.open = true;
    item.dataset.expanded = "";
    if (!content || reduceMotion()) return;
    content.getAnimations().forEach((a) => a.cancel());
    const end = content.scrollHeight;
    content.animate([{ height: `${start}px`, opacity: wasClosing ? 1 : 0.4 }, { height: `${end}px`, opacity: 1 }], { duration: DURATION, easing: EASING });
  }

  #close(item) {
    delete item.dataset.expanded;
    const content = this.#content(item);
    if (!content || reduceMotion()) {
      item.open = false;
      return;
    }
    item.dataset.closing = 'true';
    // Read the live height so closing mid-open glides from where it is.
    const start = content.getBoundingClientRect().height;
    content.getAnimations().forEach((a) => a.cancel());
    const animation = content.animate([{ height: `${start}px`, opacity: 1 }, { height: '0px', opacity: 0.4 }], { duration: DURATION, easing: EASING });
    // If the browser cancels the glide, finish closing anyway so the next tap opens it instead of being ignored.
    animation.oncancel = () => {
      if (item.dataset.closing && content.getAnimations().length === 0) {
        item.open = false;
        delete item.dataset.closing;
      }
    };
    animation.onfinish = () => {
      if (item.dataset.closing) {
        item.open = false;
        delete item.dataset.closing;
      }
    };
  }
}

if (!customElements.get('s-accordion')) customElements.define('s-accordion', SAccordion);
